import type { Period, TimetableStructure } from '@schooly/api';
import { PeriodTypeEnum, WeekdayEnum } from '@schooly/api';
import type { Row, XlsColumn, XlsSheet } from '@schooly/components/xls-service';

import { timeToMinutes } from '../../../SchoolTimetableStructures/utils';

import type { TimetableImportMessages } from './useTimetableImportMessages';

function buildTemplateColumns(msg: TimetableImportMessages): XlsColumn[] {
  return [
    { key: 'day', header: msg.columnDay, width: 15 },
    { key: 'period', header: msg.columnPeriod, width: 15 },
    { key: 'group', header: msg.columnTimetableGroupCode, width: 25 },
    { key: 'room', header: msg.columnRoom, width: 25 },
  ];
}

function buildPeriodColumns(msg: TimetableImportMessages): XlsColumn[] {
  return [
    { key: 'time', header: msg.columnTime, width: 20 },
    { key: 'name', header: msg.columnPeriodName, width: 20 },
    { key: 'number', header: msg.columnPeriodNumber, width: 15 },
    { key: 'days', header: msg.columnDays, width: 25 },
  ];
}

const WEEKDAY_TO_ISO_NUMBER: Record<WeekdayEnum, number> = {
  [WeekdayEnum.Monday]: 1,
  [WeekdayEnum.Tuesday]: 2,
  [WeekdayEnum.Wednesday]: 3,
  [WeekdayEnum.Thursday]: 4,
  [WeekdayEnum.Friday]: 5,
  [WeekdayEnum.Saturday]: 6,
  [WeekdayEnum.Sunday]: 7,
};

type BuildTemplateExportSheetsParams = {
  structure: TimetableStructure;
  ageGroupNames: string;
  msg: TimetableImportMessages;
};

type ExportPeriod = {
  index: number;
  days: number[];
} & Period;

export function buildTemplateExportSheets({
  structure,
  ageGroupNames,
  msg,
}: BuildTemplateExportSheetsParams): XlsSheet[] {
  const sortedPeriods = [...structure.periods].sort(
    (a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time),
  );

  const daySet = new Set<number>();
  const periods = sortedPeriods.reduce<ExportPeriod[]>((acc, p, i) => {
    if (p.type !== PeriodTypeEnum.Lesson && p.type !== PeriodTypeEnum.Registration) return acc;

    const days = structure.time_slots
      .filter((s) => s.period_id === p.id)
      .map((s) => WEEKDAY_TO_ISO_NUMBER[s.weekday])
      .sort((a, b) => a - b);

    for (const d of days) daySet.add(d);
    acc.push({ ...p, index: i + 1, days });
    return acc;
  }, []);

  const activeDays = [...daySet].sort((a, b) => a - b);

  const timetableRows: Row[] = [];
  for (const day of activeDays) {
    for (const { index, days } of periods) {
      if (!days.includes(day)) continue;
      timetableRows.push({ day, period: index });
    }
  }

  const periodRows: Row[] = periods.map((period) => ({
    time: `${period.start_time} - ${period.end_time}`,
    name: period.name,
    number: period.index,
    days: period.days.join(', '),
  }));

  return [
    { name: msg.sheetNameTemplate, rows: timetableRows, columns: buildTemplateColumns(msg) },
    {
      name: msg.sheetNameStructure,
      rows: periodRows,
      columns: buildPeriodColumns(msg),
      title: ageGroupNames ? msg.ageGroupsNote(ageGroupNames) : undefined,
    },
  ];
}
